import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { Config, Integration, Plan } from '../models/config.model';

@Injectable({
  providedIn: 'root',
})
export class SharedDataService {
  private configSource = new BehaviorSubject<Config | null>(null);
  private orderIdSource = new BehaviorSubject<string>('');
  private planSource = new BehaviorSubject<Plan | null>(this.loadFromLocalStorage<Plan>('plan'));
  private rangeIndexSource = new BehaviorSubject<number>(this.loadFromLocalStorage<number>('rangeIndex', 0));
  private clientSecretSource = new BehaviorSubject<string>('');
  private formInfo = new BehaviorSubject<any>(this.loadFromLocalStorage('formInfo'));
  private hasAcceptedTermsOfService = new BehaviorSubject<boolean>(this.loadFromLocalStorage<boolean>('hasAcceptedTermsOfService', false));
  private accountInfo = new BehaviorSubject<any>(this.loadFromLocalStorage('accountInfo'));
  private creditCardInfoStripe = new BehaviorSubject<any>(null);
  private envSource = new BehaviorSubject<string>('');
  private returnURLSource = new BehaviorSubject<string>(this.loadFromLocalStorage<string>('returnURL', null));
  private integrationEnabledSource = new BehaviorSubject<boolean>(this.loadFromLocalStorage<boolean>('integrationEnabled', false));
  private selectedIntegrationSource = new BehaviorSubject<Integration | null>(this.loadFromLocalStorage<Integration>('selectedIntegration', null));
  private billingTermSource = new BehaviorSubject<string | null>(this.loadFromLocalStorage<string>('billingTerm', null));
  private directEntrySource = new BehaviorSubject<boolean>(this.loadFromLocalStorage<boolean>('directEntry', false));

  constructor(private localStorageService: LocalStorageService) {}

  private loadFromLocalStorage<T>(key: string, defaultValue: T | null = null): T | null {
    const savedValue = this.localStorageService.getItem(key);
    if (savedValue === null || savedValue === undefined) {
      return defaultValue;
    }

    if (typeof defaultValue === 'boolean') {
      // If the savedValue is a boolean, return it directly.
      // If it’s a string, compare ignoring case.
      if (typeof savedValue === 'boolean') {
        return savedValue as T;
      } else if (typeof savedValue === 'string') {
        return (savedValue.toLowerCase() === 'true') as T;
      }
      return defaultValue;
    }

    return savedValue;
  }


  setIntegrationEnabled(enabled: boolean): void {
    this.integrationEnabledSource.next(enabled);
    this.localStorageService.setItem('integrationEnabled', JSON.stringify(enabled));
    if (!enabled) {
      this.setSelectedIntegration(null); // Reset selected integration if disabled
    }
  }

  getIntegrationEnabled(): Observable<boolean> {
    return this.integrationEnabledSource.asObservable();
  }

  setSelectedIntegration(integration: Integration | null): void {
    this.selectedIntegrationSource.next(integration);
    if (integration) {
      this.localStorageService.setItem('selectedIntegration', JSON.stringify(integration));
    } else {
      this.localStorageService.removeItem('selectedIntegration');
    }
  }

  getSelectedIntegration(): Observable<Integration | null> {
    return this.selectedIntegrationSource.asObservable();
  }

  setEnv(env: string): void {
    this.envSource.next(env);
  }

  getEnv():string {
    return this.envSource.value;
  }

  setReturnURL(url: string): void {
    if(url){
      this.localStorageService.setItem('returnURL', url);
    }else{
      this.localStorageService.removeItem('returnURL');
    }
    this.returnURLSource.next(url);
  }

  getReturnURL():string {
    return this.returnURLSource.value;
  }

  setConfig(config: Config): void {
    this.configSource.next(config);
  }


  getConfig(): Observable<Config> {
    return this.configSource.asObservable();
  }

  getConfigValue(): Config | null {
    return this.configSource.value;
  }

  setOrderId(orderId: string): void {
    this.orderIdSource.next(orderId);
  }

  getOrderId(): string {
    return this.orderIdSource.value;
  }

  setPlan(plan: Plan): void {
    this.localStorageService.setItem('plan', plan);
    this.planSource.next(plan);
  }

  getPlan(): Observable<Plan> {
    return this.planSource.asObservable();
  }

  setRangeIndex(index: number): void {
    this.localStorageService.setItem('rangeIndex', index);
    this.rangeIndexSource.next(index);
  }

  getRangeIndex(): Observable<number> {
    return this.rangeIndexSource.asObservable();
  }

  setClientSecret(secret: string): void {
    this.clientSecretSource.next(secret);
  }

  getClientSecret(): Observable<string> {
    return this.clientSecretSource.asObservable();
  }

  setAccountInfo(info: any): void {
    this.localStorageService.setItem('accountInfo', info);
    this.accountInfo.next(info);
  }

  getAccountInfo(): any {
    return this.accountInfo.value;
  }

  setCreditCardInfoStripe(info: any): void {
    this.localStorageService.setItem('creditCardInfoStripe', info);
    this.creditCardInfoStripe.next(info);
  }

  getCreditCardInfoStripe(): any {
    return this.creditCardInfoStripe.value;
  }

  setFormInfo(info: any): void {
    this.localStorageService.setItem('formInfo', info);
    this.formInfo.next(info);
  }

  getFormInfo(): any {
    return this.formInfo.value;
  }

  setBillingTerm(term: string | null): void {
    if (term) {
      this.localStorageService.setItem('billingTerm', term);
    } else {
      this.localStorageService.removeItem('billingTerm');
    }
    this.billingTermSource.next(term);
  }

  getBillingTerm(): string | null {
    return this.billingTermSource.value;
  }

  setDirectEntry(value: boolean): void {
    this.localStorageService.setItem('directEntry', JSON.stringify(value));
    this.directEntrySource.next(value);
  }

  isDirectEntry(): boolean {
    return this.directEntrySource.value;
  }

  setHasAcceptedTermsOfService(hasAccepted: boolean): void {
    this.localStorageService.setItem('hasAcceptedTermsOfService', hasAccepted);
    this.hasAcceptedTermsOfService.next(hasAccepted);
  }

  getHasAcceptedTermsOfService(): Observable<boolean> {
    return this.hasAcceptedTermsOfService.asObservable();
  }

}
